import React, { useContext, useMemo, useRef, useState } from 'react'
import { getProductTableFields } from './productConstant';
import { filterProductInterface, sortConfig } from './product';
import { UserContext, UserContextPayload } from '../../../../context/userContext';
import { Checkbox, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from '@mui/material';
import globalStrenameDocumentyles from "../../../../assets/renameDocument.svg";
import deleteDoc from "../../../../assets/deleteDoc.svg"; 
import dashboardStyles from '../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from '../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss';
import globalStyles from "../../../../assets/styles/global.module.scss";
import EditIcon from '@mui/icons-material/Edit';
import { lightGreyColor, tertiaryColor } from '../../../../utils/colors';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useProductStore } from '../../../../store/productStore';
import { useShallow } from 'zustand/react/shallow';
import { EcomProduct } from '../../../../models/axiosModel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MemoizedProductTableRow from './memoizedProductTableRow';
import GlobalDeletePopup from '../../../utility/globalDeletePopup/globalDeletePopup';
import agent from '../../../../services/api';
import GlobalPopUp from '../../../utility/globalPopUp';
import ProductAdditionalInfoPopup from './productAdditionalInfoPopup';
import EditProductPopup from './editProductPopup';
import { hasProductManageAccess } from '../../../../utils/constants';
import ViewQRCodeDataPopup from './viewQRCodeDataPopup';
import ViewCatalogOptionalDataPopup from './viewCatalogOptionalDataPopup';
import { useGenericStore } from '../../../../store/genericStore';

interface ProductTableProps {
    filters: filterProductInterface, 
    sortConfig: sortConfig, 
    setSortConfig: React.Dispatch<React.SetStateAction<sortConfig>>,
    setFilteredProducts: React.Dispatch<React.SetStateAction<EcomProduct[]>>,
    getEComProductApiCall: () => Promise<void>, 
    checkedProducts: EcomProduct[], 
    setCheckedProducts: React.Dispatch<React.SetStateAction<EcomProduct[]>>, 
    showDeleteConfirmation: boolean, 
    setShowDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>, 
    selectedProduct: EcomProduct | null, 
    setSelectedProduct: React.Dispatch<React.SetStateAction<EcomProduct | null>>,
    getWhatsAppCatalogsDataApiCall: () => Promise<void>,
    getCatalogProductOptionalFieldApiCall: () => Promise<void>,
}  

const ProductTable: React.FC<ProductTableProps> = (props) => {

    const { filters, sortConfig, setSortConfig, setFilteredProducts, getEComProductApiCall, checkedProducts, setCheckedProducts, showDeleteConfirmation, setShowDeleteConfirmation, selectedProduct, setSelectedProduct, getWhatsAppCatalogsDataApiCall, getCatalogProductOptionalFieldApiCall} = props; 
    const { userDetail, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;
    const { eComProductData, whatsAppCatalogsData } = useProductStore(useShallow((state) => ({
        eComProductData: state.eComProductData,
        whatsAppCatalogsData: state.whatsAppCatalogsData,
    })));

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const hasWhatsAppCatalogsData = (whatsAppCatalogsData?.length ?? 0) > 0;
    const visibleTableFields = useMemo(
        () => getProductTableFields(hasWhatsAppCatalogsData),
        [hasWhatsAppCatalogsData]
    );
    
    const breakPoints = {
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
    }

    const innerHeight = (window.innerHeight - 250);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<string | null>(null);
    const [changeField, setChangeField] = useState<string>(""); 

    // const [selectedProduct, setSelectedProduct] = useState<EcomProduct | null>(null);
    // const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [flagEditProductPopup, setFlagEditProductPopup] = useState<boolean>(false);

    const [flagViewAdditionalInfo, setFlagViewAdditionalInfo] = useState<boolean>(false);
    const [flagViewQRCodeData, setFlagViewQRCodeData] = useState<boolean>(false);
    const [flagViewOptionalData, setFlagViewOptionalData] = useState<boolean>(false);

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: string) => {
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };
    
    const menuItem = [
        {
            label: "Edit",
            icon: <img alt="" src={globalStrenameDocumentyles} />,
            action: (product: EcomProduct) => {
                setSelectedProduct(product);
                setFlagEditProductPopup(true);
            }
        },
        {
            label: "Delete",
            icon: <img alt="" src={deleteDoc} />,
            action: (product: EcomProduct) => {
                setSelectedProduct(product);
                setShowDeleteConfirmation(true);
            }
        },
        {
            label: "Copy Product ID",
            icon: <ContentCopyIcon sx={{ color: lightGreyColor, fontSize: "18px" }} />,
            action: (product: EcomProduct) => {
                navigator.clipboard.writeText(product?.ecom_product_id);
                showSuccess("Product ID copied to clipboard")
            }
        }
    ]

    const sortContacts = (key: string, direction: string, productData: EcomProduct[] | null) => {
        if (productData) { 

            const filtered_products = productData?.sort((a, b) => {
                let aValue, bValue;
                if (key === "Updated At") {
                    aValue = a.updated_at_timestamp || a.created_at_timestamp || "";
                    bValue = b.updated_at_timestamp ||  b.created_at_timestamp || "";
                } else {
                    aValue = a[key as keyof EcomProduct] || "";
                    bValue = b[key as keyof EcomProduct] || "";
                }
                if (aValue === "" && bValue === "") {
                    return 0;
                }
                if (aValue === "") {
                    return direction === 'ascending' ? -1 : 1;
                }
                if (bValue === "") {
                    return direction === 'ascending' ? 1 : -1;
                }
                if (aValue < bValue) {
                    return direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return direction === 'ascending' ? 1 : -1;
                }
                return 0;
            })
            return filtered_products;
        } else return [];
    }

    const sortData = (key: string) => {
        setChangeField(key);
        let dir = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            dir = 'descending';
        }
        setSortConfig({ key: key, direction: dir });
    }  

    const getFilteredProducts = useMemo(() => {
        let products = eComProductData ?? [];
        if (sortConfig.key.length > 0 && sortConfig.direction.length > 0) { // Sort the Data.
            products = sortContacts(sortConfig.key, sortConfig.direction, eComProductData) || [];
        } 
        const filtered_products = products?.filter((product) => {
            const productNameMatch = !filters.product_name?.length || filters.product_name?.find((filterProduct) => filterProduct.ecom_product_id == product.ecom_product_id);

            return productNameMatch;
        })
        setFilteredProducts(filtered_products);
        return filtered_products;

    }, [eComProductData, sortConfig, filters]);

    const virtualizer = useVirtualizer({
        count: getFilteredProducts.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 48,
        overscan: 10,
        measureElement:
            typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')
                ? el => el?.getBoundingClientRect().height
                : undefined,
    }); 

    const formatEComAdditionalInfo = (data: EcomProduct, key: keyof EcomProduct) => {
        const value = data[key];
        if(typeof value == "string"){
            try {
                const parsed = JSON.parse(value) as Record<string, unknown>;
                if(parsed && typeof parsed == "object"){
                    return Object.entries(parsed);
                }
            } catch {
                return value;
            }
        } 

        return [];
    }

    const onClickOfViewAdditionalInfo = (data: EcomProduct) => {
        setFlagViewAdditionalInfo(true);
        setSelectedProduct(data);
    } 

    const onClickOfViewQrCodeData = (data: EcomProduct) => {
        setFlagViewQRCodeData(true);
        setSelectedProduct(data);
    }

    const onClickOfViewOptionalData = (data: EcomProduct) => {
        setFlagViewOptionalData(true);
        setSelectedProduct(data);
    }

    const handleAllChanges = () => {
        if (getFilteredProducts?.length > 0) {
            if (checkedProducts.length === getFilteredProducts?.length) {
                setCheckedProducts([]);
            } else {
                setCheckedProducts(getFilteredProducts);
            }
        }
    } 

    const handleSelectProduct = (data: EcomProduct) => {
        setCheckedProducts((prev) => {
            if(prev?.find((product) => product.ecom_product_id == data?.ecom_product_id)){
                return prev?.filter((item) => item?.ecom_product_id !== data?.ecom_product_id);
            } else {
                return [...prev, data];
            }
        })
    }

    return (
        <>
            <TableContainer className={`${style.contactTableContainer} ${globalStyles.overflowAuto} ${globalStyles.relativePosition}`} ref={tableContainerRef} sx={{ maxHeight: `${innerHeight}px !important` }}>
                <Table stickyHeader>
                    <TableHead sx={{ position: "sticky", top: "0", zIndex: "2" }}>
                        <TableRow sx={{ display: "flex", width: "100% !important" }}> 
                            {hasProductManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                                <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.checkBoxTableCell} ${style.checkBoxCellWidth}`}>
                                    <Checkbox
                                        checked={((checkedProducts.length === getFilteredProducts?.length) || (checkedProducts.length === eComProductData?.length)) ? true : false}
                                        onChange={(e) => handleAllChanges()}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        disabled={getFilteredProducts?.length == 0 ? true : false}
                                        // disabled={(filteredCampaignContactData && filteredCampaignContactData.length === 0) ? true : false}
                                        color="secondary"
                                        className={style.checkBoxContact}
                                    />
                                </TableCell>
                            }

                            {visibleTableFields && visibleTableFields?.length > 0 && 
                                visibleTableFields?.map((product, ind) => {
                                    return (
                                        <TableCell
                                            className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth} ${product.getCallResponseKey == "ecom_product_name" && !breakPoints.flagSmallScreen ? style.firstColumnSticky : ""}`}
                                            key={ind}
                                            onClick={() => { sortData(product.getCallResponseKey) }}  
                                            sx={{zIndex: product.getCallResponseKey == "ecom_product_name" && !breakPoints.flagSmallScreen ? "5 !important" : ""}}
                                        >
                                            <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                                <Grid item>
                                                    {product.name}
                                                </Grid>
                                                <Grid item>
                                                    <Grid container className={`${globalStyles.flexColumn}`}>
                                                        <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "ascending" && product.getCallResponseKey === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▲</Typography>
                                                        <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "descending" && product.getCallResponseKey === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▼</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                    );
                                })
                            } 
                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`} onClick={() => sortData("Updated At")}>
                                <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                    <Grid item>
                                        Updated At
                                    </Grid>
                                    <Grid item>
                                        <Grid container className={`${globalStyles.flexColumn}`}>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "ascending" && "Created At" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▲</Typography>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "descending" && "Created At" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▼</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </TableCell>
                            {hasProductManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                                <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.checkBoxCellWidth}`} sx={{ background: `${tertiaryColor} !important` }}></TableCell>
                            }
                        </TableRow>
                    </TableHead> 
                    
                    <TableBody
                        sx={{
                            display: virtualizer.getVirtualItems().length == 0 ? 'contents' : 'block',
                            height: virtualizer.getVirtualItems().length == 0 ? 'auto' : `${virtualizer.getTotalSize()}px`,
                            position: virtualizer.getVirtualItems().length == 0 ? 'static' : 'relative',
                        }}
                    >
                        {virtualizer.getVirtualItems().length > 0 ?
                            virtualizer.getVirtualItems().map(virtualRow => {
                                    const data = getFilteredProducts[virtualRow.index];
                                    if (!data) return null;

                                    return (
                                        <TableRow
                                            key={data.ecom_product_id}
                                            data-index={virtualRow.index}
                                            ref={node => virtualizer.measureElement(node)}
                                            style={{
                                                position: 'absolute',
                                                transform: `translateY(${virtualRow.start}px)`,
                                                display: 'flex',
                                                width: '100%',
                                            }}
                                        >
                                            <MemoizedProductTableRow
                                                data={data}
                                                index={virtualRow.index} 
                                                userTimezone={userDetail.timezone_hour} 
                                                anchorEl={anchorEl}
                                                currentRowIndex={currentRowIndex}
                                                menuItem={menuItem}
                                                handleMenuOpen={handleMenuOpen}
                                                handleMenuClose={handleMenuClose}  
                                                formatEComAdditionalInfo={formatEComAdditionalInfo} 
                                                onClickOfViewAdditionalInfo={onClickOfViewAdditionalInfo}
                                                onClickOfViewQrCodeData={onClickOfViewQrCodeData}
                                                onClickOfViewOptionalData={onClickOfViewOptionalData}
                                                handleSelectProduct={handleSelectProduct}
                                                isMenuOpen={data.ecom_product_id == currentRowIndex}  
                                                isCheckBoxChecked={checkedProducts.find(product => product?.ecom_product_id == data.ecom_product_id) ? true : false}
                                            />
                                        </TableRow>
                                    );
                                })
                            : 
                            <TableRow className={`${dashboardStyles.cellRow}`}>
                                <TableCell colSpan={12} className={globalStyles.textCenter}>
                                    No Data Available
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>

                </Table>
            </TableContainer>

            {flagViewAdditionalInfo && 
                <GlobalPopUp
                    open={flagViewAdditionalInfo}
                    onClose={() => { setFlagViewAdditionalInfo(false); setSelectedProduct(null); }}
                    handleClose={() => { setFlagViewAdditionalInfo(false); setSelectedProduct(null);}}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "750px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "850px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <ProductAdditionalInfoPopup
                        selectedProduct={selectedProduct as EcomProduct}
                    />
                </GlobalPopUp>
            } 

            {flagViewQRCodeData && 
                <GlobalPopUp
                    open={flagViewQRCodeData}
                    onClose={() => { setFlagViewQRCodeData(false); setSelectedProduct(null); }}
                    handleClose={() => { setFlagViewQRCodeData(false); setSelectedProduct(null);}}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "750px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "850px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <ViewQRCodeDataPopup
                        selectedProduct={selectedProduct as EcomProduct}
                    />
                </GlobalPopUp>
            }

            {flagViewOptionalData && 
                <GlobalPopUp
                    open={flagViewOptionalData}
                    onClose={() => { setFlagViewOptionalData(false); setSelectedProduct(null); }}
                    handleClose={() => { setFlagViewOptionalData(false); setSelectedProduct(null);}}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "750px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "850px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <ViewCatalogOptionalDataPopup
                        selectedProduct={selectedProduct as EcomProduct}
                    />
                </GlobalPopUp>
            }

            {flagEditProductPopup && 
                <GlobalPopUp
                    open={flagEditProductPopup}
                    onClose={() => { setFlagEditProductPopup(false); setSelectedProduct(null); }}
                    handleClose={() => { setFlagEditProductPopup(false); setSelectedProduct(null);}}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "750px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "850px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <EditProductPopup
                        selectedProduct={selectedProduct as EcomProduct} 
                        getEComProductApiCall={getEComProductApiCall}  
                        setFlagEditProductPopup={setFlagEditProductPopup}
                        getWhatsAppCatalogsDataApiCall={getWhatsAppCatalogsDataApiCall}
                        getCatalogProductOptionalFieldApiCall={getCatalogProductOptionalFieldApiCall}
                    />
                </GlobalPopUp>
            }
        </>
    )
}

export default ProductTable
